import type { Infer } from 'superstruct';
import {
	boolean,
	define,
	mask,
	nullable,
	number,
	object,
	string,
	trimmed,
} from 'superstruct';

import {
	emptyStringIsNull,
	matchesField,
	password,
	userString,
} from '@adflow/shared/util-superstruct';

export const LOGIN_ATTEMPT_LIMIT = 10;
export const USER_LOCKOUT_TIME = 60 * 30 * 1000;

/**
 * Ensure that a value is an email address.
 */
export const emailAddress = (requiredErrorMessage = 'validations:Required') =>
	trimmed(
		define<string>('Email', (value, context) => {
			let validString: string;
			try {
				validString = mask(value, trimmed(emptyStringIsNull(string())));
			} catch {
				return { message: requiredErrorMessage, ...context };
			}

			// Taken from https://emailregex.com/
			const emailRegex =
				// eslint-disable-next-line require-unicode-regexp
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			if (emailRegex.test(validString)) {
				return true;
			} else {
				return { message: 'validations:Invalid Email', ...context };
			}
		})
	);

export const LoginDto = object({
	email: emailAddress(),
	password: userString('validations:Required'),
});

export type LoginDto = Infer<typeof LoginDto>;

export const RequestResetPasswordDto = object({
	email: emailAddress(),
});

export type RequestResetPasswordDto = Infer<typeof RequestResetPasswordDto>;

export const ChangePasswordFormDto = object({
	newPassword: password(),
	confirmPassword: matchesField<string>(
		'newPassword',
		'user-form:Passwords do not match.'
	),
});

export type ChangePasswordFormDto = Infer<typeof ChangePasswordFormDto>;

export const ChangePasswordDto = object({
	newPassword: password(),
	token: string(),
});

export type ChangePasswordDto = Infer<typeof ChangePasswordDto>;

export const CheckResetPasswordTokenDto = object({
	token: string(),
});

export type CheckResetPasswordTokenDto = Infer<
	typeof CheckResetPasswordTokenDto
>;

export const ResetPasswordTokenInfoDto = object({
	isValidToken: boolean(),
	user: nullable(
		object({
			id: number(),
			name: string(),
		})
	),
});
export type ResetPasswordTokenInfoDto = Infer<typeof ResetPasswordTokenInfoDto>;
